import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { useTheme } from '../../../theme/useTheme';

interface KPIOverviewLoadingSkeletonProps {
	columns: number;
}

const SECTION_CARD_ROWS = 2;

const KPIOverviewLoadingSkeleton: React.FC<KPIOverviewLoadingSkeletonProps> = ({ columns }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const pulse = useRef(new Animated.Value(0.45)).current;

	useEffect(() => {
		const animation = Animated.loop(
			Animated.sequence([
				Animated.timing(pulse, {
					toValue: 1,
					duration: 700,
					useNativeDriver: true,
				}),
				Animated.timing(pulse, {
					toValue: 0.45,
					duration: 700,
					useNativeDriver: true,
				}),
			])
		);

		animation.start();
		return () => animation.stop();
	}, [pulse]);

	const cards = useMemo(
		() => Array.from({ length: Math.max(columns * SECTION_CARD_ROWS, columns) }),
		[columns]
	);

	const skeletonBg = isDark ? colors.background.elevated : '#DCE3EE';
	const cardBg = isDark ? colors.background.surface : '#FFFFFF';
	const cardBorder = isDark ? colors.border.default : '#E3E9F3';

	return (
		<View>
			<View style={styles.sectionHeader}>
				<Animated.View style={[styles.skeletonBase, styles.headerIcon, { opacity: pulse, backgroundColor: skeletonBg }]} />
				<Animated.View style={[styles.skeletonBase, styles.headerTitle, { opacity: pulse, backgroundColor: skeletonBg }]} />
				<Animated.View style={[styles.skeletonBase, styles.headerPill, { opacity: pulse, backgroundColor: skeletonBg }]} />
			</View>

			<View style={styles.cardsGrid}>
				{cards.map((_, index) => (
					<View key={`kpi-skeleton-${index}`} style={[styles.cardColumn, { width: `${100 / columns}%` }]}>
						<View style={[styles.card, { backgroundColor: cardBg, borderColor: cardBorder }]}>
							<View style={styles.cardTopRow}>
								<View style={styles.titleRow}>
									<Animated.View style={[styles.skeletonBase, styles.categoryBadge, { opacity: pulse, backgroundColor: skeletonBg }]} />
									<Animated.View style={[styles.skeletonBase, styles.cardTitle, { opacity: pulse, backgroundColor: skeletonBg }]} />
								</View>
								<Animated.View style={[styles.skeletonBase, styles.cardActionBtn, { opacity: pulse, backgroundColor: skeletonBg }]} />
							</View>

							<View style={styles.metricHeaderRow}>
								<Animated.View style={[styles.skeletonBase, styles.metricHeader, { opacity: pulse, backgroundColor: skeletonBg }]} />
								<Animated.View style={[styles.skeletonBase, styles.metricHeader, { opacity: pulse, backgroundColor: skeletonBg }]} />
								<Animated.View style={[styles.skeletonBase, styles.metricHeader, { opacity: pulse, backgroundColor: skeletonBg }]} />
							</View>

							<View style={styles.metricValueRow}>
								<Animated.View style={[styles.skeletonBase, styles.metricValue, { opacity: pulse, backgroundColor: skeletonBg }]} />
								<Animated.View style={[styles.skeletonBase, styles.metricValue, { opacity: pulse, backgroundColor: skeletonBg }]} />
								<Animated.View style={[styles.skeletonBase, styles.metricValue, { opacity: pulse, backgroundColor: skeletonBg }]} />
							</View>
						</View>
					</View>
				))}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	skeletonBase: {
		backgroundColor: '#DCE3EE',
		borderRadius: 6,
	},
	sectionHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		marginTop: 12,
		marginBottom: 10,
	},
	headerIcon: {
		width: 18,
		height: 18,
		borderRadius: 9,
	},
	headerTitle: {
		width: 180,
		height: 18,
	},
	headerPill: {
		marginLeft: 'auto',
		width: 100,
		height: 24,
		borderRadius: 12,
	},
	cardsGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	cardColumn: {
		padding: 8,
	},
	card: {
		backgroundColor: '#FFFFFF',
		borderRadius: 12,
		padding: 16,
		borderWidth: 1,
		borderColor: '#E3E9F3',
	},
	cardTopRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 12,
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		flex: 1,
		paddingRight: 8,
	},
	categoryBadge: {
		width: 28,
		height: 28,
		borderRadius: 6,
	},
	cardTitle: {
		width: '72%',
		height: 16,
	},
	cardActionBtn: {
		width: 34,
		height: 34,
		borderRadius: 8,
	},
	metricHeaderRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	metricHeader: {
		width: '28%',
		height: 10,
	},
	metricValueRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginTop: 10,
	},
	metricValue: {
		width: '30%',
		height: 18,
	},
});

export default KPIOverviewLoadingSkeleton;
